import { useCallback, useMemo, useState } from "react";
import { IconMap2 } from "@tabler/icons-react";

import { Box, Drawer, Flex, Skeleton, Text, UnstyledButton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Banner, NoResults } from "@onengine/component-library";
import { createFileRoute } from "@tanstack/react-router";

import {
  useLiveOverviewQuery,
  useProviderHistoryQuery,
} from "@/api/live-dashboard/live-dashboard.queries";
import type {
  LiveDashboardFlag,
  LiveDashboardWorkStatus,
} from "@/api/live-dashboard/live-dashboard.types";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { useFreshnessTicker } from "@/hooks/live-dashboard/useFreshnessTicker";
import { useLiveDashboardChannel } from "@/hooks/live-dashboard/useLiveDashboardChannel";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { isEntityScoped, useEntityTenantStore } from "@/store/entityTenantStore";
import { authGuardWithPermissions } from "@/utils/authGuard";

import { LiveMapDrawer } from "@/components/features/LiveMap/LiveMapDrawer";
import { LiveMapList } from "@/components/features/LiveMap/LiveMapList";
import { LiveMapMap } from "@/components/features/LiveMap/LiveMapMap";
import { LiveMapTabs } from "@/components/features/LiveMap/LiveMapTabs";
import type {
  LiveMapRouteTrail,
  LiveMapSiteCircle,
} from "@/components/features/LiveMap/LiveMapMap";
import { LIVE_MAP_MOBILE_BREAKPOINT } from "@/components/features/LiveMap/liveMap.constants";
import { getProviderPosition } from "@/components/features/LiveMap/liveMap.utils";
import styles from "@/components/features/LiveMap/live-map.module.scss";

export const Route = createFileRoute("/_authenticated/live-map/")({
  component: LiveMap,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function LiveMap() {
  useBreadcrumb([
    {
      title: "Live Map",
      href: "/live-map",
      // IconMap2 directly, not getRouteIcon(MODULE_KEYS_MAP.dispatchQueue) (review
      // finding, SERV-3137): this route reuses the Dispatch Queue permission module (no
      // new module for v1), but Sidebar.tsx already deliberately overrides that
      // module's own IconDeviceAnalytics with IconMap2 here so the two items don't read
      // as duplicates -- the breadcrumb was still pulling the Dispatch Queue icon
      // through the shared module-icon map, showing a different icon than the sidebar
      // item that links to this same page.
      icon: <IconMap2 size={18} />,
    },
  ]);

  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const isScoped = isEntityScoped(entityTenant);

  const { isConnected } = useLiveDashboardChannel();
  const overviewQuery = useLiveOverviewQuery({ isRealtimeConnected: isConnected });
  const providers = useFreshnessTicker(overviewQuery.data);

  const isMobile = useMediaQuery(LIVE_MAP_MOBILE_BREAKPOINT);

  const [search, setSearch] = useState("");
  const [workStatusFilter, setWorkStatusFilter] = useState<LiveDashboardWorkStatus | null>(null);
  const [flagFilter, setFlagFilter] = useState<LiveDashboardFlag | null>(null);
  const [selectedServiceProviderId, setSelectedServiceProviderId] = useState<string | null>(null);
  const [showRoute, setShowRoute] = useState(false);
  const [mobileListOpen, setMobileListOpen] = useState(false);

  // Review finding (SERV-3137): a plain `new Date()` here was recomputed on every
  // render, not just the ticker's 30s tick -- any re-render while "Show route" is on
  // (typing in search, clicking a filter chip, an overview poll completing) changed
  // `now`'s millisecond value, which is part of useProviderHistoryQuery's query key,
  // so React Query treated it as a brand-new query and refired the history fetch every
  // time. Memoized to only recompute when the route toggle or selected provider
  // actually changes.
  const now = useMemo(() => new Date(), [showRoute, selectedServiceProviderId]);

  const selectedProvider =
    providers.find((provider) => provider.serviceProviderId === selectedServiceProviderId) ?? null;

  // useCallback (review finding, SERV-3137): LiveMapMap is memoized so an unrelated
  // parent re-render doesn't re-trigger its marker-recluster effect -- that only works
  // if this callback keeps a stable identity across those renders too.
  const handleSelectProvider = useCallback(
    (serviceProviderId: string) => {
      setSelectedServiceProviderId(serviceProviderId);
      setShowRoute(false);
      if (isMobile) setMobileListOpen(false);
    },
    [isMobile],
  );

  // Spec 9.7 F: "a 'Show route' toggle that calls history from the visit's start (or
  // scheduledTime) to now". The REST contract (9.6) only exposes `scheduledTime` on a
  // VisitSummary, not an actual check-in timestamp, so scheduledTime is what's used here.
  const historyFrom = selectedProvider?.currentVisit?.scheduledTime ?? null;
  const historyEnabled = showRoute && Boolean(selectedProvider?.userId) && Boolean(historyFrom);
  const historyQuery = useProviderHistoryQuery(
    historyEnabled ? (selectedProvider?.userId ?? "") : "",
    historyEnabled ? (historyFrom ?? "") : "",
    historyEnabled ? now.toISOString() : "",
  );

  const routeTrail: LiveMapRouteTrail | null = useMemo(() => {
    if (!showRoute || !historyQuery.data || historyQuery.data.length < 2) return null;
    return { path: historyQuery.data.map((point) => ({ lat: point.lat, lng: point.lng })) };
  }, [showRoute, historyQuery.data]);

  const siteCircle: LiveMapSiteCircle | null = useMemo(() => {
    const site = selectedProvider?.currentVisit?.site;
    if (!site || site.lat === null || site.lng === null || site.radiusMeters === null) return null;
    return { center: { lat: site.lat, lng: site.lng }, radiusMeters: site.radiusMeters };
  }, [selectedProvider]);

  const allPositionsMissing =
    providers.length > 0 && providers.every((provider) => getProviderPosition(provider) === null);

  if (!isScoped) {
    return (
      <Flex direction="column" className={styles.container}>
        <Box className={styles.emptyStateCard}>
          <Text size="lg" fw={600}>
            Select an entity to view its live map
          </Text>
          <Text size="sm" c="dimmed">
            The live map is scoped to one entity at a time. Choose one from the entity switcher
            above to see its service providers.
          </Text>
        </Box>
      </Flex>
    );
  }

  if (overviewQuery.isPending) {
    return (
      <Flex
        direction="column"
        className={styles.container}
        gap="var(--spacing-medium)"
        data-testid="live-map-loading-skeleton"
      >
        <LiveMapTabs activeTab="map" />
        <div className={styles.layout}>
          <Skeleton height="100%" />
          <Skeleton height="100%" />
        </div>
      </Flex>
    );
  }

  const listPanel = (
    <LiveMapList
      providers={providers}
      now={now}
      search={search}
      onSearchChange={setSearch}
      workStatusFilter={workStatusFilter}
      onWorkStatusFilterChange={setWorkStatusFilter}
      flagFilter={flagFilter}
      onFlagFilterChange={setFlagFilter}
      selectedServiceProviderId={selectedServiceProviderId}
      onSelect={handleSelectProvider}
    />
  );

  return (
    <Flex direction="column" className={styles.container} gap="var(--spacing-medium)">
      <LiveMapTabs activeTab="map" />
      {!isConnected && (
        <Banner
          intent="warn"
          title="Live updates unavailable"
          message="We'll keep refreshing the map automatically every 20 seconds."
        />
      )}
      {allPositionsMissing && (
        <Banner
          intent="danger"
          title="Location tracking is unavailable right now"
          message="Showing today's service providers without live positions."
        />
      )}

      {providers.length === 0 ? (
        <NoResults message="No service providers are on a live visit today." />
      ) : (
        <div className={styles.layout}>
          {isMobile ? (
            <Drawer
              opened={mobileListOpen}
              onClose={() => setMobileListOpen(false)}
              position="bottom"
              size="70%"
              title="Service providers"
            >
              {listPanel}
            </Drawer>
          ) : (
            <Box className={styles.listPanel}>{listPanel}</Box>
          )}

          <LiveMapMap
            providers={providers}
            selectedServiceProviderId={selectedServiceProviderId}
            onSelectProvider={handleSelectProvider}
            siteCircle={siteCircle}
            routeTrail={routeTrail}
          />
        </div>
      )}

      {isMobile && providers.length > 0 && !mobileListOpen && (
        <UnstyledButton
          type="button"
          onClick={() => setMobileListOpen(true)}
          className={styles.mobileListToggle}
        >
          <Text size="sm" fw={600}>
            Show list
          </Text>
        </UnstyledButton>
      )}

      <Drawer
        opened={selectedProvider !== null}
        onClose={() => setSelectedServiceProviderId(null)}
        position="right"
        size="sm"
      >
        {selectedProvider && (
          <LiveMapDrawer
            provider={selectedProvider}
            now={now}
            showRoute={showRoute}
            onToggleShowRoute={setShowRoute}
          />
        )}
      </Drawer>
    </Flex>
  );
}
